// Admin User Management — account list + detail/block console. Wrapped by
// <AdminGuard> in App.tsx.
//
// Email search + status filter (all/blocked/creator/admin) live in the URL
// query string so deep-links + browser-back work (mirrors
// AdminApplicationsPage). A row click opens the UserDetailDialog, which
// carries the block/unblock controls.

import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@greenhold/components";

import { EmptyState } from "../components/EmptyState";
import { UserDetailDialog } from "../features/admin/UserDetailDialog";
import {
  DEFAULT_USER_FILTERS,
  type AdminUsersFilters,
  type AdminUserStatus,
  type UserListItem,
  useAdminUsers,
} from "../features/admin/useAdminUsers";

const STATUS_OPTIONS: { value: AdminUserStatus; label: string }[] = [
  { value: "all", label: "All" },
  { value: "blocked", label: "Blocked" },
  { value: "creator", label: "Creators" },
  { value: "admin", label: "Admins" },
];

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IE", { dateStyle: "medium" });
}

function filtersFromSearchParams(params: URLSearchParams): AdminUsersFilters {
  const rawStatus = params.get("status") as AdminUserStatus | null;
  const status: AdminUserStatus =
    rawStatus && ["all", "blocked", "creator", "admin"].includes(rawStatus)
      ? rawStatus
      : "all";
  return {
    search: params.get("search") ?? undefined,
    status,
    page: Number(params.get("page") ?? "1") || 1,
    page_size:
      Number(params.get("page_size") ?? String(DEFAULT_USER_FILTERS.page_size)) ||
      DEFAULT_USER_FILTERS.page_size,
  };
}

function StatusBadges({ user }: { user: UserListItem }) {
  return (
    <span className="flex flex-wrap gap-1">
      {user.is_blocked ? (
        <Badge variant="destructive">
          Blocked
          {user.blocked_until ? ` until ${fmtDate(user.blocked_until)}` : ""}
        </Badge>
      ) : null}
      {user.is_admin ? <Badge>Admin</Badge> : null}
      {user.creator_status === "approved" ? (
        <Badge variant="success">Creator</Badge>
      ) : null}
      {!user.email_verified ? (
        <Badge variant="warning">Unverified email</Badge>
      ) : null}
      {!user.is_blocked &&
      !user.is_admin &&
      user.creator_status !== "approved" &&
      user.email_verified ? (
        <span className="opacity-50">—</span>
      ) : null}
    </span>
  );
}

export function AdminUsersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(
    () => filtersFromSearchParams(searchParams),
    [searchParams]
  );

  const query = useAdminUsers(filters);

  const selectedUserId = searchParams.get("user");

  function updateFilter<K extends keyof AdminUsersFilters>(
    key: K,
    value: AdminUsersFilters[K] | undefined
  ) {
    const next = new URLSearchParams(searchParams);
    if (value === undefined || value === "" || value === null) {
      next.delete(key);
    } else {
      next.set(key, String(value));
    }
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  }

  function goToPage(page: number) {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(Math.max(1, page)));
    setSearchParams(next);
  }

  function openUser(id: string) {
    const next = new URLSearchParams(searchParams);
    next.set("user", id);
    setSearchParams(next);
  }

  function closeUser() {
    const next = new URLSearchParams(searchParams);
    next.delete("user");
    setSearchParams(next);
  }

  return (
    <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900 p-6">
      <div className="max-w-6xl mx-auto space-y-[var(--gh-rhythm-admin,16px)]">
        <header>
          <h1 className="text-3xl font-heading font-bold">Users</h1>
          <p className="opacity-70 text-sm">
            Search accounts, inspect standing, and block or unblock.
          </p>
        </header>

        {/* Filter bar */}
        <Card>
          <CardContent className="flex flex-wrap gap-3 items-end p-4">
            <label className="text-sm">
              <span className="block mb-1 opacity-70">Email search</span>
              <input
                type="text"
                className="border rounded px-2 py-1 bg-white"
                placeholder="e.g. jane@"
                value={filters.search ?? ""}
                onChange={(e) => updateFilter("search", e.target.value)}
              />
            </label>
            <label className="text-sm">
              <span className="block mb-1 opacity-70">Status</span>
              <select
                className="border rounded px-2 py-1 bg-white"
                value={filters.status}
                onChange={(e) =>
                  updateFilter("status", e.target.value as AdminUserStatus)
                }
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </CardContent>
        </Card>

        {/* Results */}
        {query.isPending ? (
          <Card>
            <CardContent className="p-8 text-center opacity-70">
              Loading…
            </CardContent>
          </Card>
        ) : query.isError ? (
          <Card>
            <CardContent className="p-6 text-center space-y-3">
              <p className="text-destructive">
                {query.error.message ?? "Failed to load users."}
              </p>
              <Button onClick={() => query.refetch()} variant="secondary">
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : query.data.data.length === 0 ? (
          <EmptyState
            aria-label="No users"
            description="No users match these filters."
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Showing {query.data.data.length} user
                {query.data.data.length === 1 ? "" : "s"}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-brand-paper-100 text-left">
                  <tr>
                    <th className="px-4 py-2">Email</th>
                    <th className="px-4 py-2">Signed up</th>
                    <th className="px-4 py-2">Last sign in</th>
                    <th className="px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.data.map((row) => (
                    <tr
                      key={row.id}
                      className="border-t border-brand-paper-200 hover:bg-brand-paper-100 cursor-pointer"
                      onClick={() => openUser(row.id)}
                    >
                      <td className="px-4 py-2">
                        <div className="font-medium">
                          {row.display_name?.trim() || "—"}
                        </div>
                        <div className="opacity-70 font-mono text-xs break-all">
                          {row.email}
                        </div>
                      </td>
                      <td className="px-4 py-2 opacity-80">
                        {fmtDate(row.created_at)}
                      </td>
                      <td className="px-4 py-2 opacity-80">
                        {fmtDate(row.last_sign_in_at)}
                      </td>
                      <td className="px-4 py-2">
                        <StatusBadges user={row} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Pagination */}
        {query.data && (query.data.has_next || filters.page > 1) ? (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => goToPage(filters.page - 1)}
              disabled={filters.page <= 1}
            >
              ← Prev
            </Button>
            <span className="text-sm opacity-70 font-mono">
              Page {filters.page}
            </span>
            <Button
              variant="secondary"
              onClick={() => goToPage(filters.page + 1)}
              disabled={!query.data.has_next}
            >
              Next →
            </Button>
          </div>
        ) : null}
      </div>

      <UserDetailDialog selectedUserId={selectedUserId} onClose={closeUser} />
    </main>
  );
}
